import {
  CreditApplicationFieldsValues,
  BankReference,
  CreditApplication,
  InvitedSignatures,
  DynamicInvitedSignaturesFields,
} from '@dill/dill-shared';

export interface DecryptedApplicationData {
  decryptedFields: CreditApplicationFieldsValues[];
  decryptedBankReferences: BankReference[];
  decryptedInvitedSignatures: InvitedSignatures[];
}

export async function decryptCreditApplicationData(
  creditApplication: CreditApplication,
  decryptValue: (encryptedString: string, showFullValue?: boolean) => Promise<string>,
  showFullValue = true,
  keepBankRefHistory = false
): Promise<DecryptedApplicationData> {
  // ---- Decrypt fields ----
  const decryptedFields = await Promise.all(
    creditApplication.fieldValues.map(async (field) => {
      let newField = { ...field };

      if (Array.isArray(newField.fieldValue)) {
        newField.fieldValue = await Promise.all(
          newField.fieldValue.map(async (value) => {
            if (!value.isHashed) return value;

            const decrypted = await decryptValue(value.value, showFullValue);

            return {
              ...value,
              value: decrypted,
            };
          })
        );
      }

      if (newField.isHashed && newField.value) {
        const decryptedValue = await decryptValue(newField.value, showFullValue);

        newField = {
          ...newField,
          value: decryptedValue,
        };
      }

      return newField;
    })
  );

  // ---- Bank reference ----
  const decryptedBankReferences: BankReference[] = creditApplication?.bankReference?.length
    ? await Promise.all(
        creditApplication.bankReference.map(async (bankRef) => {
          const { approvalStatusHistory, ...bankRefWithoutAdminFields } = bankRef as any;
          const token = await decryptValue(bankRef.bankDetails.accountNumber, showFullValue);
          return {
            ...(keepBankRefHistory ? (bankRef as any) : bankRefWithoutAdminFields),
            bankDetails: {
              ...bankRef.bankDetails,
              accountNumber: token,
            },
          };
        })
      )
    : [];

  // ---- Invited signatures ----
  const decryptedInvitedSignatures: InvitedSignatures[] = creditApplication?.invitedSignatures
    ?.length
    ? await Promise.all(
        creditApplication.invitedSignatures.map(async (sig) => {
          const decryptedSignatureFields = await Promise.all(
            (sig.fields ?? []).map(async (field: DynamicInvitedSignaturesFields) => {
              if (field.isHashed && field.value) {
                const decryptedValue = await decryptValue(field.value, showFullValue);
                return { ...field, value: decryptedValue };
              }
              return field;
            })
          );
          return { ...sig, fields: decryptedSignatureFields };
        })
      )
    : [];

  return {
    decryptedFields,
    decryptedBankReferences,
    decryptedInvitedSignatures,
  };
}
